import React from 'react';
import { Author } from '../../types/community';
import styled from 'styled-components';

interface PostHeaderProps {
  title: string;
  author: Author;
  createdAt: string;
  replyCount: number;
  viewCount: number;
  lecture?: {
    id: number;
    title: string;
    chapter?: string;
  };
  category?: string;
}

const PostHeader: React.FC<PostHeaderProps> = ({
  title,
  author,
  createdAt,
  replyCount,
  viewCount,
  lecture,
  category = "커뮤니티"
}) => {
  return (
    <HeaderContainer>
      {/* 브래드크럼 */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>홈</BreadcrumbItem>
          <BreadcrumbSeparator>
            <svg width="6" height="12" viewBox="0 0 6 12" fill="none">
              <path d="M1 1L5 6L1 11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </BreadcrumbSeparator>
          <BreadcrumbItem>커뮤니티</BreadcrumbItem>
          <BreadcrumbSeparator>
            <svg width="6" height="12" viewBox="0 0 6 12" fill="none">
              <path d="M1 1L5 6L1 11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </BreadcrumbSeparator>
          <BreadcrumbItem>{category}</BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* 제목 */}
      <TitleSection>
        <Title>{title}</Title>
      </TitleSection>

      {/* 작성자 정보 및 메타 정보 */}
      <MetaSection>
        <AuthorInfo>
          <Avatar>
            <img 
              src={author.profileImage || "/images/default-avatar.png"} 
              alt={author.name}
            />
          </Avatar>
          <AuthorDetails>
            <AuthorName>{author.name}</AuthorName>
            <PostDate>{createdAt}</PostDate>
          </AuthorDetails>
        </AuthorInfo>
        
        <StatsInfo>
          <StatItem>답변 {replyCount}</StatItem>
          <StatDivider />
          <StatItem>조회수 {viewCount}</StatItem>
        </StatsInfo>
      </MetaSection>

      {/* 구분선 */}
      <Divider />
    </HeaderContainer>
  );
};

// 스타일 컴포넌트들
const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const Breadcrumb = styled.nav`
  width: 100%;
  display: flex;
  align-items: center;
`;

const BreadcrumbList = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0.01em;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const BreadcrumbSeparator = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(55, 56, 60, 0.61);
`;

const TitleSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
  margin: 0;
  word-break: keep-all;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 22px;
    line-height: 1.35;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const MetaSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(112, 115, 124, 0.08);
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const AuthorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const AuthorName = styled.span`
  color: rgba(46, 47, 51, 0.88);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.01em;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const PostDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: 0.01em;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const StatsInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  
  @media (max-width: 640px) {
    align-self: flex-start;
  }
`;

const StatItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: 0.01em;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 12px;
  background-color: rgba(112, 115, 124, 0.22);
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: rgba(112, 115, 124, 0.15);
  margin-top: 8px;
`;

export default PostHeader;